// S2 · "Preview pack" drawer (Module 02 §4 S2): a full read-only view of what a
// pack contains, across the five tabs the spec names — Steps, Units, QC, Waste,
// Terminology — before anyone installs anything. UX-1: the tab strip is the
// shared `Tabs` primitive (one tablist implementation, gate §4.4) and every
// table is the `DataTable` primitive, which carries the §3.3 phone-collapse
// contract (header dropped, per-cell data-label) the responsive net asserts.
//
// It reads `GET /v1/verticals/{key}` (onboarding.view; any office role may
// preview) through the generated api-client, and parses it defensively via
// `readPackDetail` because the contract types the pack body as an open record.
//
// Accessibility: a labelled dialog; Escape closes it; focus moves to the close
// button on open and returns to the opener on close; Tab is trapped inside.

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Tabs } from "../../../../components/tabs";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { requestIdOf } from "../../../../lib/request";
import { useVertical } from "../../hooks/onboarding-queries";
import { localeText, readPackDetail, type PackDetailView } from "./install-model";

const TABS = ["steps", "units", "qc", "waste", "terminology"] as const;
type Tab = (typeof TABS)[number];

export function PackPreviewDrawer({
  packKey,
  fallbackName,
  onClose,
}: {
  packKey: string;
  /** The list-response name, shown while the detail loads. */
  fallbackName: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const query = useVertical(packKey);
  const [tab, setTab] = useState<Tab>("steps");
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Remember the Preview button so focus goes back to it on close — landing
    // at the top of the document instead loses a keyboard user's place.
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => {
      opener.current?.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      // Trap Tab: without it, tabbing past the last control walks into the page
      // behind the drawer while the drawer still covers it.
      if (event.key !== "Tab" || panelRef.current === null) return;
      const focusable = [
        ...panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const pack: PackDetailView = readPackDetail(query.data);
  const locale = i18n.language;
  const title = localeText(pack.manifest?.name, locale) ?? fallbackName;

  return (
    <div
      className="vp-drawer-scrim"
      data-testid="pack-preview"
      onClick={onClose}
      role="presentation"
    >
      <aside
        ref={panelRef}
        className="vp-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${t("onboarding.vertical.drawer.ariaLabel")}: ${title}`}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <header className="vp-drawer-head">
          <div>
            <h2>{title}</h2>
            <dl className="vp-drawer-facts">
              <div>
                <dt>{t("onboarding.vertical.drawer.version")}</dt>
                <dd>{pack.manifest?.version ?? "—"}</dd>
              </div>
              <div>
                <dt>{t("onboarding.vertical.drawer.currency")}</dt>
                <dd>{pack.manifest?.defaultCurrency ?? "—"}</dd>
              </div>
              <div>
                <dt>{t("onboarding.vertical.drawer.measuredKinds")}</dt>
                <dd>
                  {pack.manifest === undefined || pack.manifest.measuredStockUnitKinds.length === 0
                    ? t("onboarding.vertical.drawer.none")
                    : pack.manifest.measuredStockUnitKinds
                        .map((kind) => enumLabel(t, "onboarding.vertical.measuredKind", kind))
                        .join(", ")}
                </dd>
              </div>
            </dl>
            {/* §3.2: the tenant's own currency is fixed at provisioning by
                Module 15 and is NOT changed by installing a pack. */}
            <p className="vp-note">{t("onboarding.vertical.drawer.currencyNote")}</p>
          </div>
          <button type="button" className="ds-btn" ref={closeRef} onClick={onClose}>
            {t("onboarding.vertical.drawer.close")}
          </button>
        </header>

        <Tabs
          label={t("onboarding.vertical.drawer.ariaLabel")}
          tabs={TABS.map((name) => ({
            id: name,
            domId: `vp-tab-${name}`,
            label: t(`onboarding.vertical.drawer.tabs.${name}`),
          }))}
          active={tab}
          onSelect={(id) => {
            setTab(id as Tab);
          }}
          controls="vp-tabpanel"
        />

        <div
          className="vp-drawer-body"
          id="vp-tabpanel"
          role="tabpanel"
          aria-labelledby={`vp-tab-${tab}`}
        >
          {query.isPending ? (
            <CardSkeleton lines={6} label={t("onboarding.vertical.drawer.loading")} />
          ) : query.error !== null ? (
            <ErrorState
              title={t("onboarding.vertical.drawer.errorTitle")}
              detail={t("onboarding.vertical.drawer.errorDetail")}
              retryLabel={t("onboarding.state.retry")}
              onRetry={() => void query.refetch()}
              requestId={requestIdOf(query.error)}
              requestIdLabel={t("onboarding.state.requestId")}
            />
          ) : (
            <TabBody tab={tab} pack={pack} locale={locale} />
          )}
        </div>
      </aside>
    </div>
  );
}

function TabBody({ tab, pack, locale }: { tab: Tab; pack: PackDetailView; locale: string }) {
  switch (tab) {
    case "steps":
      return <StepsTab pack={pack} locale={locale} />;
    case "units":
      return <UnitsTab pack={pack} locale={locale} />;
    case "qc":
      return <QcTab pack={pack} locale={locale} />;
    case "waste":
      return <WasteTab pack={pack} locale={locale} />;
    case "terminology":
      return <TerminologyTab pack={pack} locale={locale} />;
  }
}

function StepsTab({ pack, locale }: { pack: PackDetailView; locale: string }) {
  const { t } = useTranslation();
  type Row = PackDetailView["steps"][number];
  if (pack.steps.length === 0) {
    return (
      <EmptyState
        title={t("onboarding.vertical.drawer.steps.emptyTitle")}
        body={t("onboarding.vertical.drawer.steps.emptyBody")}
      />
    );
  }
  const columns: readonly DataTableColumn<Row>[] = [
    {
      key: "name",
      header: t("onboarding.vertical.drawer.steps.colName"),
      id: true,
      render: (step) => localeText(step.labels, locale) ?? step.ref,
    },
    {
      key: "ref",
      header: t("onboarding.vertical.drawer.steps.colRef"),
      render: (step) => <code className="vp-mono">{step.ref}</code>,
    },
    {
      key: "wc",
      header: t("onboarding.vertical.drawer.steps.colWorkCenter"),
      render: (step) => <code className="vp-mono">{step.workCenterTemplateRef ?? "—"}</code>,
    },
    {
      key: "setup",
      header: t("onboarding.vertical.drawer.steps.colSetup"),
      render: (step) =>
        step.setupMinutes === undefined
          ? "—"
          : t("onboarding.vertical.drawer.steps.setupMinutes", { minutes: step.setupMinutes }),
    },
    {
      key: "run",
      header: t("onboarding.vertical.drawer.steps.colRun"),
      render: (step) =>
        step.runSecondsPerUnit === undefined
          ? "—"
          : t("onboarding.vertical.drawer.steps.runRate", {
              seconds: step.runSecondsPerUnit,
              unit: step.runUnitCode ?? "—",
            }),
    },
    {
      key: "scrap",
      header: t("onboarding.vertical.drawer.steps.colScrap"),
      render: (step) =>
        step.operationScrapPct === undefined
          ? "—"
          : t("onboarding.vertical.drawer.steps.scrapPct", { pct: step.operationScrapPct }),
    },
    {
      key: "captures",
      header: t("onboarding.vertical.drawer.steps.colCaptures"),
      render: (step) =>
        step.captures.length === 0
          ? "—"
          : step.captures
              .map((capture) => enumLabel(t, "onboarding.vertical.capture", capture))
              .join(", "),
    },
    {
      key: "qc",
      header: t("onboarding.vertical.drawer.steps.colQc"),
      render: (step) =>
        step.qcChecklistRef === undefined ? (
          "—"
        ) : (
          <code className="vp-mono">{step.qcChecklistRef}</code>
        ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={pack.steps}
      rowKey={(step) => step.ref}
      caption={t("onboarding.vertical.drawer.tabs.steps")}
      empty={null}
    />
  );
}

function UnitsTab({ pack, locale }: { pack: PackDetailView; locale: string }) {
  const { t } = useTranslation();
  type Row = PackDetailView["units"][number];
  if (pack.units.length === 0) {
    return (
      <EmptyState
        title={t("onboarding.vertical.drawer.units.emptyTitle")}
        body={t("onboarding.vertical.drawer.units.emptyBody")}
      />
    );
  }
  const columns: readonly DataTableColumn<Row>[] = [
    {
      key: "code",
      header: t("onboarding.vertical.drawer.units.colCode"),
      id: true,
      render: (unit) => <code className="vp-mono">{unit.code}</code>,
    },
    {
      key: "name",
      header: t("onboarding.vertical.drawer.units.colName"),
      render: (unit) => localeText(unit.labels, locale) ?? unit.code,
    },
    {
      key: "kind",
      header: t("onboarding.vertical.drawer.units.colKind"),
      render: (unit) =>
        unit.kind === undefined ? "—" : enumLabel(t, "onboarding.vertical.unitKind", unit.kind),
    },
    {
      key: "precision",
      header: t("onboarding.vertical.drawer.units.colPrecision"),
      numeric: true,
      render: (unit) => unit.precision ?? "—",
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={pack.units}
      rowKey={(unit) => unit.code}
      caption={t("onboarding.vertical.drawer.tabs.units")}
      empty={null}
    />
  );
}

function QcTab({ pack, locale }: { pack: PackDetailView; locale: string }) {
  const { t } = useTranslation();
  type Row = PackDetailView["qcChecklists"][number];
  if (pack.qcChecklists.length === 0) {
    return (
      <EmptyState
        title={t("onboarding.vertical.drawer.qc.emptyTitle")}
        body={t("onboarding.vertical.drawer.qc.emptyBody")}
      />
    );
  }
  const yes = t("onboarding.vertical.drawer.qc.yes");
  const no = t("onboarding.vertical.drawer.qc.no");
  const columns: readonly DataTableColumn<Row>[] = [
    {
      key: "name",
      header: t("onboarding.vertical.drawer.qc.colName"),
      id: true,
      render: (qc) => (
        <>
          {localeText(qc.labels, locale) ?? qc.ref}
          {qc.items.length > 0 ? (
            <ul className="vp-chip-list">
              {qc.items.map((item) => (
                <li className="vp-count-chip" key={item.key}>
                  {localeText(item.labels, locale) ?? item.key}
                  {item.min !== undefined && item.max !== undefined
                    ? ` · ${t("onboarding.vertical.drawer.qc.range", {
                        min: item.min,
                        max: item.max,
                        unit: item.unitCode ?? "",
                      })}`
                    : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ),
    },
    {
      key: "ref",
      header: t("onboarding.vertical.drawer.qc.colRef"),
      render: (qc) => <code className="vp-mono">{qc.ref}</code>,
    },
    {
      key: "point",
      header: t("onboarding.vertical.drawer.qc.colControlPoint"),
      render: (qc) =>
        qc.controlPoint === undefined
          ? "—"
          : enumLabel(t, "onboarding.vertical.controlPoint", qc.controlPoint),
    },
    {
      key: "items",
      header: t("onboarding.vertical.drawer.qc.colItems"),
      numeric: true,
      render: (qc) => t("onboarding.vertical.drawer.qc.itemCount", { count: qc.items.length }),
    },
    {
      key: "fail",
      header: t("onboarding.vertical.drawer.qc.colFailAction"),
      render: (qc) =>
        qc.defaultFailAction === undefined
          ? "—"
          : enumLabel(t, "onboarding.vertical.failAction", qc.defaultFailAction),
    },
    {
      key: "cut",
      header: t("onboarding.vertical.drawer.qc.colGuidedCut"),
      render: (qc) => (qc.allowGuidedCut === true ? yes : no),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={pack.qcChecklists}
      rowKey={(qc) => qc.ref}
      caption={t("onboarding.vertical.drawer.tabs.qc")}
      empty={null}
    />
  );
}

function WasteTab({ pack, locale }: { pack: PackDetailView; locale: string }) {
  const { t } = useTranslation();
  type Row = PackDetailView["wasteCategories"][number];
  if (pack.wasteCategories.length === 0) {
    return (
      <EmptyState
        title={t("onboarding.vertical.drawer.waste.emptyTitle")}
        body={t("onboarding.vertical.drawer.waste.emptyBody")}
      />
    );
  }
  const yes = t("onboarding.vertical.drawer.waste.yes");
  const no = t("onboarding.vertical.drawer.waste.no");
  const columns: readonly DataTableColumn<Row>[] = [
    {
      key: "name",
      header: t("onboarding.vertical.drawer.waste.colName"),
      id: true,
      render: (waste) => localeText(waste.labels, locale) ?? waste.ref,
    },
    {
      key: "ref",
      header: t("onboarding.vertical.drawer.waste.colRef"),
      render: (waste) => <code className="vp-mono">{waste.ref}</code>,
    },
    {
      key: "disposition",
      header: t("onboarding.vertical.drawer.waste.colDisposition"),
      render: (waste) =>
        waste.dispositionDefault === undefined
          ? "—"
          : enumLabel(t, "onboarding.vertical.disposition", waste.dispositionDefault),
    },
    {
      key: "recoverable",
      header: t("onboarding.vertical.drawer.waste.colRecoverable"),
      render: (waste) => (waste.recoverable === true ? yes : no),
    },
    {
      key: "dimensions",
      header: t("onboarding.vertical.drawer.waste.colDimensions"),
      render: (waste) => (waste.tracksDimensions === true ? yes : no),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={pack.wasteCategories}
      rowKey={(waste) => waste.ref}
      caption={t("onboarding.vertical.drawer.tabs.waste")}
      empty={null}
    />
  );
}

function TerminologyTab({ pack, locale }: { pack: PackDetailView; locale: string }) {
  const { t } = useTranslation();
  type Row = PackDetailView["terminology"][number];
  if (pack.terminology.length === 0) {
    return (
      <EmptyState
        title={t("onboarding.vertical.drawer.terminology.emptyTitle")}
        body={t("onboarding.vertical.drawer.terminology.emptyBody")}
      />
    );
  }
  const columns: readonly DataTableColumn<Row>[] = [
    {
      key: "key",
      header: t("onboarding.vertical.drawer.terminology.colKey"),
      id: true,
      render: (term) => (
        <>
          {/* The BASE label of the canonical key, from the app dictionary
              (§2.5); the pack's own label is the next column. */}
          {t(`onboarding.terms.${term.termKey}`, { defaultValue: term.termKey })}
          <br />
          <code className="vp-mono">{term.termKey}</code>
        </>
      ),
    },
    {
      key: "label",
      header: t("onboarding.vertical.drawer.terminology.colLabel"),
      render: (term) => localeText(term.labels, locale) ?? "—",
    },
    {
      key: "other",
      header: t("onboarding.vertical.drawer.terminology.colOtherLocales"),
      render: (term) => (
        <span className="vp-muted vp-mono">
          {Object.entries(term.labels)
            .filter(([code]) => code !== locale)
            .map(([code, label]) => `${code}: ${label}`)
            .join(" · ")}
        </span>
      ),
    },
  ];
  return (
    <>
      <p className="vp-note">{t("onboarding.vertical.drawer.terminology.note")}</p>
      <DataTable
        columns={columns}
        rows={pack.terminology}
        rowKey={(term) => term.termKey}
        caption={t("onboarding.vertical.drawer.tabs.terminology")}
        empty={null}
      />
    </>
  );
}
